import React, {Suspense, lazy, useEffect, useRef, useState} from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  Link,
  useLocation,
  useParams,
} from "react-router-dom";
import {CartProvider, useCart} from "./context/CartContext";

// Store
import Header from "./components/Header";
import Home from "./pages/Home";
import ProductList from "./pages/ProductList";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import TrackOrder from "./pages/TrackOrder";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AboutUs from "./pages/AboutUs";
import TermsAndConditions from "./pages/TermsAndConditions";

// Admin
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminProductForm from "./pages/admin/AdminProductForm";
import AdminImageManager from "./pages/admin/AdminImageManager";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminOrders from "./pages/admin/AdminOrders";
import VideoCallWidget from "./components/VideoCallWidget";

import ContactUs from "./pages/ContactUs";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminCoupons from "./pages/admin/AdminCoupons";

import ShippingLabel from "./pages/admin/ShippingLabel";
import "./App.css";
import CartDrawer from "./components/CartDrawer";
import AnnouncementBar from "./components/AnnouncementBar";
import {takeThemeReturn} from "./themes/returnKey";

// Showcase themes — separate lazy chunks, so the main store never loads them
const ThemeGallery = lazy(() => import("./themes/ThemeGallery"));
const THEMES = {
  v1: lazy(() => import("./themes/v1/ThemeV1")),
  v2: lazy(() => import("./themes/v2/ThemeV2")),
  v3: lazy(() => import("./themes/v3/ThemeV3")),
  v4: lazy(() => import("./themes/v4/ThemeV4")),
  v5: lazy(() => import("./themes/v5/ThemeV5")),
};

const ANNOUNCEMENTS = [
  "🚚 Free shipping on orders above ₹699",
  "🚚 Dispatch in 2 to 3 days",
  // "🎨 Made to order · dispatched in 3–7 days",
  // "🌱 100% recyclable PLA",
];

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSent(true);
    setEmail("");
  }

  return (
    <form className="footer-newsletter-form" onSubmit={handleSubmit}>
      <input
        type="email"
        required
        placeholder={sent ? "You're on the list ✓" : "Your email address"}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <button type="submit" className="btn-pill solid">
        Subscribe
      </button>
    </form>
  );
}

// React Router doesn't reset scroll position on navigation like a normal
// page load does — without this, clicking a link from partway down a page
// leaves the new (often shorter) page scrolled to the same offset, which
// the browser clamps to its bottom.
function ScrollToTop() {
  const {pathname} = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// The Meta Pixel snippet in index.html only fires PageView on the first
// full page load; client-side route changes need to report their own.
function MetaPixelPageView() {
  const {pathname} = useLocation();
  const isFirstLoad = useRef(true);
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    if (typeof window.fbq === "function") window.fbq("track", "PageView");
  }, [pathname]);
  return null;
}

// Cashfree always returns to /order-success. If the payment was started from a
// showcase theme, send the shopper back to that theme's own result page.
// Main-store shoppers never have the key set, so their flow is unchanged.
function OrderSuccessRoute() {
  const {orderNumber} = useParams();
  const {search} = useLocation();
  const [base] = useState(takeThemeReturn);
  if (base && THEMES[base.replace("/", "")])
    return <Navigate to={`${base}/order-success/${orderNumber}${search}`} replace />;
  return <OrderSuccess />;
}

function StoreLayout() {
  return (
    <>
      <AnnouncementBar messages={ANNOUNCEMENTS} header={true} />
      <Header />
      <main className="app-main">
        <Outlet />
        <CartDrawer />
        {/* <VideoCallWidget /> */}
      </main>
      <footer className="app-footer">
        <div className="footer-newsletter">
          <div className="footer-newsletter-inner">
            <div className="footer-newsletter-text">
              <h3>Get first dibs on new drops</h3>
              <p>One email a month. No spam, just new designs.</p>
            </div>
            <NewsletterForm />
          </div>
        </div>

        <div className="footer-inner">
          <div className="footer-top">
            <div>
              <div className="footer-brand-mark">Variant Exclusive</div>
              <p className="footer-tagline">
                Bold décor & gifts for people who refuse to be ordinary — made
                just for you, delivered across India.
              </p>
              <div className="footer-social-row">
                <a
                  className="footer-social-btn"
                  href="https://www.instagram.com/variant_exclusiv?igsi=bmQyZGd4YmtqazNs&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <div className="footer-col-title">Shop</div>
              <div className="footer-col-links">
                <Link to="/products">All Products</Link>
                <Link to="/products?bestseller=true">Bestsellers</Link>
                <Link to="/cart">Cart</Link>
                <Link to="/track-order">Track Order</Link>
              </div>
            </div>

            <div>
              <div className="footer-col-title">Company</div>
              <div className="footer-col-links">
                <Link to="/about-us">About Us</Link>
                <Link to="/contact">Contact</Link>
              </div>
            </div>

            <div>
              <div className="footer-col-title">Legal</div>
              <div className="footer-col-links">
                <Link to="/privacy-policy">Privacy Policy</Link>
                <Link to="/terms">Terms & Conditions</Link>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <span>© 2026 Variant Exclusive. All rights reserved.</span>
            <span>Made with care in India</span>
          </div>
        </div>
      </footer>
    </>
  );
}

export default function App() {
  return (
    <CartProvider>
      <BrowserRouter>
        <ScrollToTop />
        <MetaPixelPageView />
        <Routes>
          {/* Store */}
          <Route element={<StoreLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<ProductList />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route
              path="/order-success/:orderNumber"
              element={<OrderSuccessRoute />}
            />
            <Route path="/track-order" element={<TrackOrder />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/terms" element={<TermsAndConditions />} />
          </Route>

          {/* Admin */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/:id" element={<AdminProductForm />} />
            <Route path="products/:id/images" element={<AdminImageManager />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route
              path="orders/:orderNumber/label"
              element={<ShippingLabel />}
            />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="coupons" element={<AdminCoupons />} />
          </Route>

          {/* Showcase themes — same products, cart and order flow, new UI */}
          <Route
            path="/themes"
            element={
              <Suspense fallback={null}>
                <ThemeGallery />
              </Suspense>
            }
          />
          {Object.entries(THEMES).map(([key, Theme]) => (
            <Route
              key={key}
              path={`/${key}/*`}
              element={
                <Suspense fallback={null}>
                  <Theme />
                </Suspense>
              }
            />
          ))}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </CartProvider>
  );
}
